// runtime-overlay-boards.js — full-screen broadcast boards burnt into the video:
// IPL-style innings break, winner card and match summary. Split from runtime-core.js.
import { get } from 'svelte/store';
import { fullScreenBoardStartedAt } from './svelte/store.js';
import { appSettings } from './store.js';
import { computeResult, formatOvers } from './scoring-logic.js';
import { getCachedLogo } from './runtime-cache.js';
import { rgba, clipText, roundRect } from './runtime-draw-utils.js';
import { teamObj, teamColor, teamLogo, battingTeamLetter } from './runtime-teams.js';

export function _drawInningsBreakBoard(ctx, W, H, cx, m) {
  // Dark gradient backdrop
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "rgba(2,6,23,0.97)");
  bg.addColorStop(1, "rgba(15,23,42,0.97)");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  const inn1 = m.innings[0];
  const battingTeam = teamObj(battingTeamLetter(inn1));
  const nextBatTeam = inn1.battingTeam === "A" ? m.teamB : m.teamA;
  const battingColor = teamColor(battingTeam.name);
  const target = inn1.totalRuns + 1;

  // Top accent
  const stripe = ctx.createLinearGradient(0, 0, W, 0);
  stripe.addColorStop(0, battingColor);
  stripe.addColorStop(1, teamColor(nextBatTeam.name));
  ctx.fillStyle = stripe;
  ctx.fillRect(0, 0, W, 6);
  ctx.fillRect(0, H - 6, W, 6);

  // Header
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillStyle = "#fbbf24";
  ctx.font = "900 24px Outfit, sans-serif";
  ctx.fillText("END OF FIRST INNINGS", cx, 28);

  // Batting team header — logo + name + big score
  const teamRowY = 80;
  const logoSize = 70;
  const logo = getCachedLogo(teamLogo(battingTeam.name));
  if (logo) {
    ctx.drawImage(logo, cx - 280, teamRowY - 6, logoSize, logoSize);
  } else {
    ctx.fillStyle = battingColor;
    roundRect(ctx, cx - 280, teamRowY - 6, logoSize, logoSize, 10); ctx.fill();
  }
  ctx.textAlign = "left";
  ctx.fillStyle = battingColor;
  ctx.font = "900 30px Outfit, sans-serif";
  ctx.fillText(battingTeam.name.toUpperCase(), cx - 190, teamRowY);
  ctx.fillStyle = "#94a3b8";
  ctx.font = "700 14px sans-serif";
  ctx.fillText("BATTING FIRST", cx - 190, teamRowY + 36);

  // Big score — right side
  ctx.textAlign = "right";
  ctx.fillStyle = "#fff";
  ctx.font = "900 56px Outfit, sans-serif";
  ctx.fillText(`${inn1.totalRuns}/${inn1.wickets}`, cx + 280, teamRowY - 4);
  ctx.fillStyle = "#94a3b8";
  ctx.font = "800 16px sans-serif";
  ctx.fillText(`(${formatOvers(inn1.balls)} ov, RR ${(inn1.totalRuns / Math.max(0.1, inn1.balls/6)).toFixed(2)})`, cx + 280, teamRowY + 50);

  // Divider
  ctx.strokeStyle = "rgba(255,255,255,0.12)";
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(cx - 290, teamRowY + 80); ctx.lineTo(cx + 290, teamRowY + 80); ctx.stroke();

  // Two-column performers — batters | bowlers
  const colY = teamRowY + 96;
  ctx.textAlign = "left";
  ctx.fillStyle = battingColor;
  ctx.font = "900 14px sans-serif";
  ctx.fillText("TOP BATTERS", cx - 280, colY);
  ctx.fillStyle = teamColor(nextBatTeam.name);
  ctx.fillText("TOP BOWLERS", cx + 20, colY);

  const topBats = [...inn1.batsmen].filter(b => b.balls > 0 || b.runs > 0).sort((a,b) => b.runs - a.runs).slice(0, 4);
  const topBowls = [...inn1.bowlers].filter(b => b.balls > 0).sort((a,b) => b.wickets - a.wickets || a.runs - b.runs).slice(0, 4);

  ctx.fillStyle = "#e5e7eb";
  ctx.font = "800 16px sans-serif";
  topBats.forEach((b, i) => {
    const y = colY + 26 + i * 26;
    ctx.fillText(clipText(ctx, b.name, 200), cx - 280, y);
    ctx.textAlign = "right";
    ctx.fillStyle = "#fbbf24";
    ctx.fillText(`${b.runs}${b.out ? "" : "*"} (${b.balls})`, cx - 20, y);
    ctx.textAlign = "left";
    ctx.fillStyle = "#e5e7eb";
  });
  topBowls.forEach((b, i) => {
    const y = colY + 26 + i * 26;
    ctx.fillText(clipText(ctx, b.name, 200), cx + 20, y);
    ctx.textAlign = "right";
    ctx.fillStyle = "#86efac";
    ctx.fillText(`${b.wickets}-${b.runs} (${formatOvers(b.balls)})`, cx + 280, y);
    ctx.textAlign = "left";
    ctx.fillStyle = "#e5e7eb";
  });

  // TARGET banner — bottom, huge
  const tBoxY = H - 180;
  const tBoxH = 130;
  ctx.fillStyle = "rgba(251,191,36,0.12)";
  roundRect(ctx, 60, tBoxY, W - 120, tBoxH, 10); ctx.fill();
  ctx.strokeStyle = "#fbbf24";
  ctx.lineWidth = 2;
  roundRect(ctx, 60, tBoxY, W - 120, tBoxH, 10); ctx.stroke();
  ctx.textAlign = "center";
  ctx.fillStyle = "#fbbf24";
  ctx.font = "900 16px Outfit, sans-serif";
  ctx.fillText("TARGET", cx, tBoxY + 14);
  ctx.fillStyle = "#fff";
  ctx.font = "900 56px Outfit, sans-serif";
  ctx.fillText(`${nextBatTeam.name.toUpperCase()} NEED ${target}`, cx, tBoxY + 40);
  ctx.fillStyle = "#fbbf24";
  ctx.font = "800 18px sans-serif";
  ctx.fillText(`Required Run Rate: ${(target / m.overs).toFixed(2)} from ${m.overs}.0 overs`, cx, tBoxY + 100);
}

export function _drawWinnerBoard(ctx, W, H, cx, m) {
  // Time-based pulse for trophy and team name
  const t = ((Date.now() - (get(fullScreenBoardStartedAt) || Date.now())) / 1000);
  const pulse = 1 + Math.sin(t * 2) * 0.04;

  const res = m.result || computeResult(m);
  const isTie = res && res.winner === "TIE";
  const winName = isTie ? null : (res.winner === "A" ? m.teamA.name : m.teamB.name);
  const winColor = winName ? teamColor(winName) : "#fbbf24";

  // Backdrop — dark + radial team-color glow behind logo
  ctx.fillStyle = "rgba(2,6,23,0.97)";
  ctx.fillRect(0, 0, W, H);
  if (winName) {
    const glow = ctx.createRadialGradient(cx, H * 0.45, 40, cx, H * 0.45, W * 0.55);
    glow.addColorStop(0, rgba(winColor, 0.30));
    glow.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);
  }

  // Confetti dots — simple animated particles
  for (let i = 0; i < 30; i++) {
    const seed = (i * 137.5) % 360;
    const px = (Math.sin(seed) * 0.5 + 0.5) * W;
    const py = ((seed * 7 + t * 100) % H);
    const sz = 3 + (i % 4);
    ctx.fillStyle = i % 2 === 0 ? winColor : "#fbbf24";
    ctx.globalAlpha = 0.6;
    ctx.beginPath(); ctx.arc(px, py, sz, 0, Math.PI * 2); ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Trophy at top, pulsing
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.save();
  ctx.translate(cx, H * 0.16);
  ctx.scale(pulse, pulse);
  ctx.font = "120px sans-serif";
  ctx.fillText("🏆", 0, 0);
  ctx.restore();

  if (isTie) {
    ctx.fillStyle = "#fbbf24";
    ctx.font = "900 80px Outfit, sans-serif";
    ctx.fillText("MATCH TIED", cx, H * 0.50);
    return;
  }

  // BIG team logo (or coloured tile fallback)
  const logo = getCachedLogo(teamLogo(winName));
  const logoCY = H * 0.46;
  const logoSize = 240;
  if (logo) {
    ctx.save();
    ctx.translate(cx, logoCY);
    ctx.scale(pulse, pulse);
    // Glow ring
    ctx.shadowColor = winColor;
    ctx.shadowBlur = 40;
    ctx.fillStyle = "rgba(255,255,255,0.05)";
    ctx.beginPath(); ctx.arc(0, 0, logoSize / 2 + 12, 0, Math.PI * 2); ctx.fill();
    ctx.shadowBlur = 0;
    ctx.drawImage(logo, -logoSize / 2, -logoSize / 2, logoSize, logoSize);
    ctx.restore();
  } else {
    ctx.save();
    ctx.translate(cx, logoCY);
    ctx.scale(pulse, pulse);
    ctx.fillStyle = winColor;
    ctx.shadowColor = winColor;
    ctx.shadowBlur = 30;
    roundRect(ctx, -logoSize / 2, -logoSize / 2, logoSize, logoSize, 20); ctx.fill();
    ctx.shadowBlur = 0;
    ctx.restore();
  }

  // WINNER label
  ctx.fillStyle = "#fbbf24";
  ctx.font = "900 42px Outfit, sans-serif";
  ctx.fillText("WINNER", cx, H * 0.70);

  // Team name (huge, glowing)
  ctx.save();
  ctx.fillStyle = winColor;
  ctx.shadowColor = winColor;
  ctx.shadowBlur = 30;
  ctx.font = "900 64px Outfit, sans-serif";
  ctx.fillText(winName.toUpperCase(), cx, H * 0.80);
  ctx.restore();

  // Margin
  ctx.fillStyle = "#e5e7eb";
  ctx.font = "800 26px Outfit, sans-serif";
  if (res) {
    let msg = "";
    if (res.marginType === "challenge") {
      const inn = m.innings[0];
      const won = res.winner === inn.battingTeam;
      msg = won ? "CHALLENGE COMPLETED! 🎉" : "CHALLENGE FAILED! ❌";
    } else {
      msg = `WINS BY ${res.margin} ${res.marginType.toUpperCase()}`;
    }
    ctx.fillText(msg, cx, H * 0.89);
  }
}

export function _drawMatchSummaryBoard(ctx, W, H, cx, m) {
  // Backdrop
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "rgba(2,6,23,0.97)");
  bg.addColorStop(1, "rgba(15,23,42,0.97)");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  if (m.mode === "challenge") {
    // DRAW CHALLENGE SUMMARY
    const inn = m.innings[0];
    const c = m.challenge || {};
    const battingTeam = m.teamA;
    const bowlingTeam = m.teamB;
    const c1 = teamColor(battingTeam.name);
    const c2 = teamColor(bowlingTeam.name);

    // Accent stripe
    const stripe = ctx.createLinearGradient(0, 0, W, 0);
    stripe.addColorStop(0, c1);
    stripe.addColorStop(1, c2);
    ctx.fillStyle = stripe;
    ctx.fillRect(0, 0, W, 6);
    ctx.fillRect(0, H - 6, W, 6);

    // Header
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillStyle = "#fbbf24";
    ctx.font = "900 24px Outfit, sans-serif";
    ctx.fillText("CHALLENGE SUMMARY", cx, 28);

    // Draw batsman details
    const logoA = getCachedLogo(teamLogo(battingTeam.name));
    const logoSize = 64;
    const row1Y = 100;
    if (logoA) {
      ctx.drawImage(logoA, 80, row1Y - 4, logoSize, logoSize);
    } else {
      ctx.fillStyle = c1;
      roundRect(ctx, 80, row1Y - 4, logoSize, logoSize, 10); ctx.fill();
    }
    ctx.textAlign = "left";
    ctx.fillStyle = c1;
    ctx.font = "900 26px Outfit, sans-serif";
    ctx.fillText(battingTeam.name.toUpperCase(), 160, row1Y);

    // Display total runs and sixes
    const totalSixes = inn.batsmen.reduce((sum, b) => sum + b.sixes, 0);
    ctx.textAlign = "right";
    ctx.fillStyle = "#fff";
    ctx.font = "900 40px Outfit, sans-serif";
    ctx.fillText(`${inn.totalRuns} Runs`, W - 80, row1Y - 8);
    ctx.fillStyle = "#94a3b8";
    ctx.font = "800 16px sans-serif";
    ctx.fillText(`${totalSixes} Sixes | ${formatOvers(inn.balls)} ov`, W - 80, row1Y + 34);

    // Draw bowler details
    const logoB = getCachedLogo(teamLogo(bowlingTeam.name));
    const row2Y = 200;
    if (logoB) {
      ctx.drawImage(logoB, 80, row2Y - 4, logoSize, logoSize);
    } else {
      ctx.fillStyle = c2;
      roundRect(ctx, 80, row2Y - 4, logoSize, logoSize, 10); ctx.fill();
    }
    ctx.textAlign = "left";
    ctx.fillStyle = c2;
    ctx.font = "900 26px Outfit, sans-serif";
    ctx.fillText(bowlingTeam.name.toUpperCase(), 160, row2Y);
    ctx.fillStyle = "#94a3b8";
    ctx.font = "700 13px sans-serif";
    ctx.fillText("BOWLED CHALLENGE OVER", 160, row2Y + 32);

    // Wickets/Out status
    ctx.textAlign = "right";
    ctx.fillStyle = "#fff";
    ctx.font = "900 40px Outfit, sans-serif";
    ctx.fillText(`${inn.wickets} Wkt`, W - 80, row2Y - 8);

    // Divider
    ctx.strokeStyle = "rgba(255,255,255,0.12)";
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(60, 290); ctx.lineTo(W - 60, 290); ctx.stroke();

    // Winner banner
    const res = m.result || computeResult(m);
    const isTie = res && res.winner === "TIE";
    const winName = isTie ? null : (res.winner === "A" ? m.teamA.name : m.teamB.name);
    const winColor = winName ? teamColor(winName) : "#fbbf24";
    const banY = 320;
    const banH = 90;
    ctx.fillStyle = rgba(winColor, 0.15);
    roundRect(ctx, 60, banY, W - 120, banH, 10); ctx.fill();
    ctx.strokeStyle = winColor;
    ctx.lineWidth = 2;
    roundRect(ctx, 60, banY, W - 120, banH, 10); ctx.stroke();
    ctx.textAlign = "center";
    ctx.fillStyle = winColor;
    ctx.font = "900 34px Outfit, sans-serif";
    if (isTie) {
      ctx.fillText("🏆 CHALLENGE TIED", cx, banY + 30);
    } else {
      ctx.fillText(`🏆 ${winName.toUpperCase()} WINS`, cx, banY + 18);
      ctx.fillStyle = "#fff";
      ctx.font = "900 24px Outfit, sans-serif";
      let detail = "";
      if (c.type === 'target_sixes') detail = `Hit ${totalSixes}/${c.targetValue} Sixes`;
      else if (c.type === 'target_runs') detail = `Scored ${inn.totalRuns}/${c.targetValue} Runs`;
      else if (c.type === 'target_wickets') detail = `Took ${inn.wickets}/${c.targetValue} Wickets`;
      ctx.fillText(detail, cx, banY + 56);
    }

    // Top performer display
    const potm = inn.batsmen[0];
    if (potm) {
      const pY = 440;
      ctx.textAlign = "center";
      ctx.fillStyle = "#fbbf24";
      ctx.font = "900 16px Outfit, sans-serif";
      ctx.fillText("⭐ CHALLENGE BATSMAN", cx, pY);
      ctx.fillStyle = "#fff";
      ctx.font = "900 32px Outfit, sans-serif";
      ctx.fillText(potm.name.toUpperCase(), cx, pY + 24);
      ctx.fillStyle = "#94a3b8";
      ctx.font = "800 18px sans-serif";
      ctx.fillText(`${potm.runs} runs (${potm.balls} balls, ${potm.fours} fours, ${potm.sixes} sixes)`, cx, pY + 56);
    }

    // Footer
    ctx.fillStyle = "#64748b";
    ctx.font = "700 11px sans-serif";
    ctx.fillText("CHALLENGE MODE   •   CRICKET SCORER BROADCAST", cx, H - 24);
    return;
  }

  const inn1 = m.innings[0];
  const inn2 = m.innings[1];
  const team1 = teamObj(battingTeamLetter(inn1));
  const c1 = teamColor(team1.name);

  // Top accent stripe
  const stripe = ctx.createLinearGradient(0, 0, W, 0);
  stripe.addColorStop(0, c1);
  if (inn2) {
    const team2 = teamObj(battingTeamLetter(inn2));
    stripe.addColorStop(1, teamColor(team2.name));
  } else {
    stripe.addColorStop(1, c1);
  }

  ctx.fillStyle = stripe;
  ctx.fillRect(0, 0, W, 6);
  ctx.fillRect(0, H - 6, W, 6);

  // Header
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillStyle = "#fbbf24";
  ctx.font = "900 24px Outfit, sans-serif";
  ctx.fillText("MATCH SUMMARY", cx, 28);
  ctx.fillStyle = "#94a3b8";
  ctx.font = "700 13px sans-serif";
  ctx.fillText(appSettings.venue ? "📍 " + appSettings.venue : "", cx, 58);

  // Function to draw one team's row (logo + name + score + top performer)
  const drawTeamRow = (inn, team, color, rowY) => {
    const logo = getCachedLogo(teamLogo(team.name));
    const logoSize = 64;
    // Logo
    if (logo) {
      ctx.drawImage(logo, 80, rowY - 4, logoSize, logoSize);
    } else {
      ctx.fillStyle = color;
      roundRect(ctx, 80, rowY - 4, logoSize, logoSize, 10); ctx.fill();
    }
    // Team name + score
    ctx.textAlign = "left";
    ctx.fillStyle = color;
    ctx.font = "900 26px Outfit, sans-serif";
    ctx.fillText(team.name.toUpperCase(), 160, rowY);
    // Big score on the right
    ctx.textAlign = "right";
    ctx.fillStyle = "#fff";
    ctx.font = "900 44px Outfit, sans-serif";
    ctx.fillText(`${inn.totalRuns}/${inn.wickets}`, W - 80, rowY - 8);
    ctx.fillStyle = "#94a3b8";
    ctx.font = "700 14px sans-serif";
    ctx.fillText(`(${formatOvers(inn.balls)} ov, RR ${(inn.totalRuns / Math.max(0.1, inn.balls/6)).toFixed(2)})`, W - 80, rowY + 34);
    // Top batter + top bowler (bowlers are stored per-innings — they bowled IN
    // this innings, so no cross-innings lookup is needed).
    const topBat = [...inn.batsmen].sort((a, b) => b.runs - a.runs)[0];
    const topBowl = [...inn.bowlers]
      .filter(b => b.balls > 0)
      .sort((a, b) => (b.wickets - a.wickets) || (a.runs - b.runs))[0];
    ctx.textAlign = "left";
    ctx.fillStyle = "#94a3b8";
    ctx.font = "700 13px sans-serif";
    const parts = [];
    if (topBat) parts.push(`Top bat: ${topBat.name} ${topBat.runs}(${topBat.balls})`);
    if (topBowl && topBowl.wickets > 0) parts.push(`Top bowl: ${topBowl.name} ${topBowl.wickets}-${topBowl.runs}`);
    ctx.fillText(parts.join("   "), 160, rowY + 32);
  };

  drawTeamRow(inn1, team1, c1, 96);
  if (inn2) {
    const team2 = teamObj(battingTeamLetter(inn2));
    drawTeamRow(inn2, team2, teamColor(team2.name), 200);
  }

  // Divider
  ctx.strokeStyle = "rgba(255,255,255,0.12)";
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(60, 290); ctx.lineTo(W - 60, 290); ctx.stroke();

  // Winner banner — big in middle
  const res = m.result || computeResult(m);
  const isTie = res && res.winner === "TIE";
  const winName = isTie ? null : (res.winner === "A" ? m.teamA.name : m.teamB.name);
  const winColor = winName ? teamColor(winName) : "#fbbf24";
  const banY = 320;
  const banH = 90;
  ctx.fillStyle = rgba(winColor, 0.15);
  roundRect(ctx, 60, banY, W - 120, banH, 10); ctx.fill();
  ctx.strokeStyle = winColor;
  ctx.lineWidth = 2;
  roundRect(ctx, 60, banY, W - 120, banH, 10); ctx.stroke();
  ctx.textAlign = "center";
  ctx.fillStyle = winColor;
  ctx.font = "900 34px Outfit, sans-serif";
  if (isTie) {
    ctx.fillText("🏆 MATCH TIED", cx, banY + 30);
  } else {
    ctx.fillText(`🏆 ${winName.toUpperCase()} WIN`, cx, banY + 18);
    ctx.fillStyle = "#fff";
    ctx.font = "900 24px Outfit, sans-serif";
    ctx.fillText(`BY ${res.margin} ${res.marginType.toUpperCase()}`, cx, banY + 56);
  }

  // Player of the Match (highest impact — most runs OR most wickets)
  const allBats = [...inn1.batsmen, ...inn2.batsmen].sort((a,b) => b.runs - a.runs);
  const allBowls = [...inn1.bowlers, ...inn2.bowlers].sort((a,b) => b.wickets - a.wickets || a.runs - b.runs);
  const potmBat = allBats[0];
  const potmBowl = allBowls[0];
  // Simple impact score
  const batScore = potmBat ? potmBat.runs : 0;
  const bowlScore = potmBowl ? potmBowl.wickets * 25 : 0;
  const potm = batScore >= bowlScore ? potmBat : potmBowl;
  const potmIsBatter = batScore >= bowlScore;

  if (potm) {
    const pY = 440;
    ctx.textAlign = "center";
    ctx.fillStyle = "#fbbf24";
    ctx.font = "900 16px Outfit, sans-serif";
    ctx.fillText("⭐ PLAYER OF THE MATCH", cx, pY);
    ctx.fillStyle = "#fff";
    ctx.font = "900 32px Outfit, sans-serif";
    ctx.fillText(potm.name.toUpperCase(), cx, pY + 24);
    ctx.fillStyle = "#94a3b8";
    ctx.font = "800 18px sans-serif";
    if (potmIsBatter) {
      ctx.fillText(`${potm.runs} runs (${potm.balls} balls, ${potm.fours} fours, ${potm.sixes} sixes)`, cx, pY + 56);
    } else {
      ctx.fillText(`${potm.wickets}/${potm.runs} in ${formatOvers(potm.balls)} overs`, cx, pY + 56);
    }
  }

  // Footer
  ctx.fillStyle = "#64748b";
  ctx.font = "700 11px sans-serif";
  ctx.fillText("FFPL SEASON 3   •   FLASH FIRE PREMIERE LEAGUE", cx, H - 24);
}

